import { Injectable } from '@angular/core';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import * as moment from 'moment';
import { InventorySummaryTableBaseItem } from '../pages/inventory-summary/inventory-summary.component';

@Injectable({
  providedIn: 'root'
})
export class InventorySummaryPdfService {

  constructor() {
    (pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;
  }

  // Relative widths, converted to percentages that always sum to 100% —
  // Product/Barcode get more room (longest text), Alpha the least.
  private readonly columnWeights: { [header: string]: number } = {
    'Barcode': 12,
    'Status': 8,
    'Product': 18,
    'Lot': 9,
    'Alpha': 6,
    'Order #': 9,
    'Weight': 8,
    'Quantity': 8,
    'Created Date': 10,
  };

  public buildDocDefinition(rows: InventorySummaryTableBaseItem[]): any {
    const header = ['Barcode', 'Status', 'Product', 'Lot', 'Alpha', 'Order #', 'Weight', 'Quantity', 'Created Date'];
    const weights = header.map(h => this.columnWeights[h]);
    const totalWeight = weights.reduce((sum, w) => sum + w, 0);
    const widths = weights.map(w => `${(w / totalWeight * 100).toFixed(2)}%`);

    const body: any[] = [
      header.map(h => ({ text: h, style: 'sectionHeader' }))
    ];

    rows.forEach(row => {
      body.push([
        row.barcode || '',
        row.inventoryStatusName || '',
        row.itemName || '',
        row.lotNumber || '',
        row.alphaLotAttribute || '',
        row.orderNumber || '',
        { text: this.formatNumber(row.weight), alignment: 'right' },
        { text: this.formatNumber(row.quantity), alignment: 'right' },
        { text: this.formatDate(row.createdDate), alignment: 'center' },
      ]);
    });

    return {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [24, 50, 24, 30],
      header: () => ({
        text: 'Inventory Summary Report',
        bold: true,
        alignment: 'center',
        fontSize: 12,
        margin: [0, 16, 0, 0]
      }),
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: '', width: '*' },
          { text: `Page ${currentPage} of ${pageCount}`, alignment: 'center', fontSize: 8 },
          { text: '', width: '*' }
        ],
        margin: [0, 6, 0, 0]
      }),
      styles: {
        sectionHeader: { bold: true, fontSize: 8, fillColor: '#E3E8E9' }
      },
      defaultStyle: {
        fontSize: 7.5
      },
      content: [
        {
          table: {
            headerRows: 1,
            widths,
            body
          },
          layout: {
            hLineColor: () => '#d9d9d9',
            vLineColor: () => '#d9d9d9',
            hLineWidth: () => 0.5,
            vLineWidth: () => 0.5,
            paddingLeft: () => 4,
            paddingRight: () => 4,
            paddingTop: () => 2,
            paddingBottom: () => 2
          }
        }
      ]
    };
  }

  public downloadPdf(docDefinition: any, filename: string): Promise<void> {
    return new Promise((resolve) => {
      pdfMake.createPdf(docDefinition).download(filename, () => resolve());
    });
  }

  private formatNumber(val: number | null | undefined): string {
    if (val === null || val === undefined || (val as any) === '') return '';
    return Number(val).toLocaleString('en-US', { maximumFractionDigits: 0 });
  }

  private formatDate(val: Date | string | null | undefined): string {
    if (!val) return '';
    const m = moment(val);
    return m.isValid() ? m.format('MM/DD/YYYY') : '';
  }
}
